/* eslint-disable tailwindcss/no-arbitrary-value */
import { useEffect, useState } from 'react';

import Section from 'molecules/Section';

import ResourceCard from 'components/Cards/ResourceCard';
import Filters from 'components/Resources/Filters';
import Pagination from 'components/Resources/Pagination';

import type { ContentfulTaxonomyTag } from 'graphqlTypes';
import type { SectionProps } from 'molecules/Section';
import type { FC } from 'react';
import type { PostUnion } from 'types';

interface PostListingProps extends SectionProps {
  activeTag?: ContentfulTaxonomyTag;
  authorSlug?: string;
  contentful_id?: string;
  styles?: string;
  currentPageNumber: number;
  hasPreviousPage: boolean;
  posts: PostUnion[];
  resourceType: string;
  tags: ContentfulTaxonomyTag[];
  totalPages: number;
}

const PostListing: FC<PostListingProps> = ({
  activeTag,
  allPosts,
  authorSlug,
  contentful_id,
  styles,
  currentPageNumber,
  posts,
  resourceType,
  tags,
  pageNumber,
  filters,
  search,
  sort,
}) => {
  const [sortValue, setSortValue] = useState(sort || 'Most Recent');
  const [filterValues, setFilterValues] = useState({
    industry: filters?.industry,
    tags: filters?.tags || [],
  });
  const [filteredPosts, setFilteredPosts] = useState(posts);
  const [searchValue, setSearchValue] = useState(search || '');
  const [paginatedPosts, setPaginatedPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(currentPageNumber);
  const postsPerPage = 9;

  const setToBasePage = () => {
    setCurrentPage(1);

    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', authorSlug ? `/${resourceType}/${authorSlug}` : `/${resourceType}`);
    }
  };

  const sortPosts = postsToSort => {
    let sortedPosts;

    if (sortValue === 'Oldest') {
      sortedPosts = postsToSort?.sort((a, b) => new Date(a.publishDate) - new Date(b.publishDate));
    } else {
      sortedPosts = postsToSort?.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
    }

    return sortedPosts;
  };

  const searchedPosts = (postsToSearch, value) => {
    const lowerSearchValue = value.toLowerCase();

    return postsToSearch.filter(({ author, body, title, summary }) => {
      const authorCopy = author?.fullName?.toLowerCase() || '';
      const titleCopy = title?.toLowerCase() || '';
      const bodyCopy = body?.raw?.toLowerCase() || '';
      const summaryCopy = summary?.raw?.toLowerCase() || '';

      return (
        authorCopy?.includes(lowerSearchValue) ||
        titleCopy?.includes(lowerSearchValue) ||
        bodyCopy?.includes(lowerSearchValue) ||
        summaryCopy?.includes(lowerSearchValue)
      );
    });
  };

  const filterPostsByTags = () => {
    if (filterValues.industry || filterValues.tags.length > 0) {
      const searchPosts = searchedPosts(allPosts, searchValue);
      const postsByFilter = searchPosts?.filter(post =>
        filterValues.tags.every(tag => post?.tags?.some(postTag => postTag?.name === tag)),
      );

      setFilteredPosts(sortPosts(postsByFilter));
    } else {
      setFilteredPosts(sortPosts(searchValue.length > 0 ? searchedPosts(allPosts, searchValue) : allPosts));
    }
  };

  useEffect(() => {
    filterPostsByTags();

    const pathname = window.location.pathname;
    const page = parseInt(pathname.split('/').filter(Boolean).at(-1)) || 1;

    setCurrentPage(page);
  }, [filterValues]);

  useEffect(() => {
    setFilteredPosts(sortPosts(filteredPosts));
  }, [sortValue]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * postsPerPage;
    const endIndex = startIndex + postsPerPage;
    const newPaginatedPosts = filteredPosts?.slice(startIndex, endIndex);

    setPaginatedPosts(newPaginatedPosts);
  }, [filteredPosts, pageNumber, sortValue, currentPage]);

  useEffect(() => {
    if (searchValue.length > 0) {
      const filteredBySearchPosts = searchedPosts(allPosts, searchValue);

      setFilteredPosts(filteredBySearchPosts);
    } else {
      filterPostsByTags();
    }
  }, [searchValue]);

  const generateUrl = () => {
    if (authorSlug) {
      return `/author/${authorSlug}`;
    }

    if (activeTag) {
      return `/${resourceType}/${activeTag?.name?.toLowerCase()?.replace(/\s+/g, '-')}`;
    }

    return `/${resourceType}`;
  };

  const totalPages = Math.ceil((filteredPosts?.length || 1) / postsPerPage);
  const hasNextPage = currentPage < totalPages;

  return (
    <>
      <Section
        background="none"
        padding="none"
        containerClass={`!pt-0 !px-4 sm:!px-8 !pb-24 ${styles || ''}`}
        contentfulId={contentful_id}
      >
        <div className="flex w-full flex-col justify-between gap-0 lg:gap-4">
          <Filters
            authorSlug={authorSlug}
            activeSort={sortValue}
            activeTag={activeTag}
            tags={tags}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            resourceType={resourceType}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            setSortValue={setSortValue}
            setToBasePage={setToBasePage}
          />
          <div className="flex w-full flex-col items-center gap-12">
            <div className="flex w-full flex-col items-center justify-between sm:flex-row">
              <div className="flex flex-col gap-3">
                <div className="anchor-element lg:top-[-150px]" id="listing" />
              </div>
            </div>
            {paginatedPosts?.length > 0 ? (
              <div className="flex w-full flex-col gap-8 sm:grid sm:grid-cols-3">
                {paginatedPosts?.map(post => <ResourceCard key={post?.id} {...post} />)}
              </div>
            ) : (
              <div className="flex w-full flex-col">
                <p className="text-center text-xl">No results found</p>
              </div>
            )}
            {totalPages > 1 && (
              <Pagination
                baseUrl={generateUrl()}
                currentPage={currentPage || 1}
                totalPages={totalPages}
                hasNextPage={hasNextPage}
                linkState={{ filterValues, sort: sortValue, search: searchValue }}
                setCurrentPage={setCurrentPage}
              />
            )}
          </div>
        </div>
      </Section>
    </>
  );
};

export default PostListing;
